/**
 * Module dependencies.
 */

import {
  Box,
  color,
  media,
  units,
  useBreakpoint
} from '@untile/react-components';

import {
  Button,
  ExternalLink,
  Loading,
  Navigation,
  Richtext,
  Subtitle,
  Type
} from 'src/components/core';

import { GetStaticPaths, GetStaticProps, NextPage } from 'next/types';
import { NewsExhibitionsProps } from 'src/types/news-exhibitions';
import { dateToString } from 'src/core/utils/formatter';
import { fetchNewsDetail } from 'src/api/news-exhibitions/fetch-news-exhibitions-detail';
import {
  fetchNewsExhibitions,
  paginationLimit
} from 'src/api/news-exhibitions/fetch-news-exhibitions';
import { ifNotProp, ifProp, theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useMemo } from 'react';
import { useRouter } from 'next/router';
import Container from 'src/components/core/layout/container';
import Item from 'src/components/downloads/download-item';
import Metatags from 'src/components/core/metatags';
import NewsExhibitionsCard from 'src/components/cards/news-exhibitions-card';
import NewsExhibitionsCarousel from 'src/components/news-exhibitions/news-exhibitions-detail/carousel';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import styled, { css } from 'styled-components';
import useNewsDetail from 'src/api/news-exhibitions/use-news-exhibitions-detail';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  data: NewsExhibitionsProps;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section<{ isLoading: boolean }>`
  background-color: ${color('backgrounds.taupe01')};
  padding: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(6)}) 0
    ${units(8)};
  position: relative;

  ${media.min('md')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(
    2
  )}) 0 ${units(19)};
  `}

  ${ifProp(
    'isLoading',
    css`
      min-height: 100vh;
    `
  )}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div<{ hasCarousel: boolean }>`
  display: grid;
  grid-template-areas:
    '.        title        .'
    '.        subtitle     .'
    '.        info         .'
    'carousel carousel     carousel'
    '.        description  .'
    '.        button       .'
    '.        links        .'
    '.        downloads    .'
    '.        prevNextNews .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;

  ${ifNotProp(
    'hasCarousel',
    css`
      grid-template-areas:
        '. title        .'
        '. subtitle     .'
        '. info         .'
        '. carousel     .'
        '. description  .'
        '. button       .'
        '. links        .'
        '. downloads    .'
        '. prevNextNews .';
    `
  )}

  ${media.min('md')`
    grid-template-areas:
      '.        backButton   backButton   .            .            .'
      '.        title        title        title        .            .'
      '.        subtitle     subtitle     subtitle     .            .'
      '.        info         info         info         .            .'
      'carousel carousel     carousel     carousel     carousel     carousel'
      '.        description  description  .            .            .'
      '.        button       button       .            .            .'
      '.        links        links        .            .            .'
      '.        downloads    .            .            .            .'
      '.        prevNextNews prevNextNews prevNextNews prevNextNews .';
    grid-template-columns: 1fr 6fr repeat(2, 1fr) 2fr 1fr;

    ${ifNotProp(
      'hasCarousel',
      css`
        grid-template-areas:
          '. backButton   backButton   .            .            .'
          '. title        title        title        .            .'
          '. subtitle     subtitle     subtitle     .            .'
          '. info         info         info         .            .'
          '. carousel     carousel     carousel     carousel     .'
          '. description  description  .            .            .'
          '. button       button       .            .            .'
          '. links        links        .            .            .'
          '. downloads    .            .            .            .'
          '. prevNextNews prevNextNews prevNextNews prevNextNews .';
      `
    )}
  `}
`;

/**
 * `NavigationWrapper` styled component.
 */

const NavigationWrapper = styled.div`
  grid-area: backButton;
  margin-bottom: ${units(3)};
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;

  ${media.min('md')`
    margin-bottom: ${units(1)};
  `}
`;

/**
 * `InfoWrapper` styled component.
 */

const InfoWrapper = styled.div`
  color: ${color('grey400')};
  display: flex;
  grid-area: info;
  grid-gap: ${units(2)};
  margin-bottom: ${units(6)};
  padding-top: ${units(1)};

  ${media.min('md')`
    margin-bottom: ${units(9)};
  `}
`;

/**
 * `CarouselWrapper` styled component.
 */

const CarouselWrapper = styled.div`
  grid-area: carousel;

  ${media.min('md')`
    margin: 0 auto;
    max-width: 80%;
    width: 100%;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled.div`
  color: ${color('textColor')};
  grid-area: description;
  margin-bottom: ${units(6)};
  padding-top: ${units(4)};

  ${media.min('ms')`
    margin-bottom: ${units(5)};
    padding-top: ${units(8)};
  `}

  * {
    margin: 0 0 ${units(5.5)};

    ${media.min('ms')`
      margin-bottom: ${units(5)};
    `}

    &:last-child {
      margin-bottom: 0;
    }
  }

  h5 {
    ${theme('typography.otherStyles.subtitle2')}

    margin-bottom: 10px;

    ${media.min('ms')`
      margin-bottom: ${units(1)};
    `}
  }

  h6 {
    ${theme('typography.styles.small')}

    color: ${color('grey400')};
  }
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  grid-area: button;
`;

/**
 * `StyledExternalLink` styled component.
 */

const StyledExternalLink = styled(ExternalLink)`
  color: ${color('textColor')};

  > span {
    color: ${color('grey500')};
  }
`;

/**
 * `PrevNextGrid` styled component.
 */

const PrevNextGrid = styled.div`
  display: grid;
  grid-gap: ${units(8)};
  grid-template-columns: 1fr;

  ${media.min('ms')`
    grid-gap: ${units(3)};
    grid-template-columns: repeat(2, 1fr);
  `}
`;

/**
 * `NewsExhibitionsDetail` page.
 */

const NewsExhibitionsDetail: NextPage<Props> = props => {
  const { locale, translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const { query } = useRouter();
  const isMobile = useBreakpoint('md', 'max');
  const { data, isLoading, isSuccess } = useNewsDetail({
    initialData: props.data,
    slug: get(query, 'slug') as string
  });

  const hasCarousel = useMemo(() => {
    return (
      size(data?.featuredImages) > 1 ||
      (!!data?.videoUrl && !isEmpty(data?.featuredImages))
    );
  }, [data?.featuredImages, data?.videoUrl]);

  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  return (
    <>
      <Metatags
        description={data?.descriptionSeo ? data?.descriptionSeo : data?.lead}
        pageTitle={data?.title}
        title={data?.titleSeo ? data?.titleSeo : data?.title}
      />

      <Wrapper isLoading={isLoading}>
        <Loading active={isLoading} />

        {isSuccess && (
          <StyledContainer>
            <Grid hasCarousel={hasCarousel}>
              {!isMobile && (
                <NavigationWrapper {...animProps}>
                  <Navigation
                    href={routeResolver('newsExhibitions')}
                    label={translate('common:actions.goBack')}
                  />
                </NavigationWrapper>
              )}

              <Title {...animProps}>{data?.title}</Title>

              <Subtitle
                {...animProps}
                gridArea={'subtitle'}
              >
                {data?.lead}
              </Subtitle>

              <InfoWrapper {...animProps}>
                <Type.Paragraph>
                  {dateToString(new Date(data?.date), locale)}
                </Type.Paragraph>

                <Type.Paragraph>
                  {translate(`news-exhibitions:categories.${data?.category}`)}
                </Type.Paragraph>
              </InfoWrapper>

              <CarouselWrapper
                data-scroll={'fade-in-up-small'}
                data-scroll-offset={'20%'}
              >
                <NewsExhibitionsCarousel {...data} />
              </CarouselWrapper>

              <Description
                data-scroll={'fade-in-up-small'}
                data-scroll-offset={'20%'}
              >
                <Richtext>{data?.body}</Richtext>
              </Description>

              {data?.linkLabel && data?.linkUrl && (
                <ButtonWrapper {...animProps}>
                  <Button
                    colorTheme={'secondary'}
                    href={data?.linkUrl}
                    size={'large'}
                  >
                    {data?.linkLabel}
                  </Button>
                </ButtonWrapper>
              )}

              {!isEmpty(data?.links) && (
                <Box
                  gridArea={'links'}
                  paddingTop={units(6)}
                  paddingTopMs={units(9)}
                >
                  <Subtitle
                    {...animProps}
                    marginBottom={units(2)}
                    size={'large'}
                  >
                    {translate('news-exhibitions:labels.usefulLinks')}
                  </Subtitle>

                  <Box
                    {...animProps}
                    display={'flex'}
                    flexDirection={'column'}
                    gridGap={units(1)}
                  >
                    {map(data?.links, ({ attributes, key }) => (
                      <div
                        {...animProps}
                        key={key}
                      >
                        <StyledExternalLink href={attributes?.linkUrl}>
                          {attributes?.linkLabel}
                        </StyledExternalLink>
                      </div>
                    ))}
                  </Box>
                </Box>
              )}

              {!isEmpty(data?.downloads) && (
                <Box
                  gridArea={'downloads'}
                  maxWidth={units(90)}
                  paddingTop={units(6)}
                  paddingTopMs={units(9)}
                >
                  <Subtitle
                    {...animProps}
                    marginBottom={units(2)}
                    size={'large'}
                  >
                    {translate('news-exhibitions:labels.downloads')}
                  </Subtitle>

                  <Box
                    {...animProps}
                    display={'flex'}
                    flexDirection={'column'}
                    gridGap={units(1)}
                  >
                    {map(data?.downloads, ({ file, id, name }) => (
                      <div
                        key={id}
                        {...animProps}
                      >
                        <Item
                          size={file.size}
                          title={name}
                          url={file.url}
                        />
                      </div>
                    ))}
                  </Box>
                </Box>
              )}

              {(data?.previousNews || data?.nextNews) && (
                <Box
                  gridArea={'prevNextNews'}
                  paddingTop={units(8)}
                  paddingTopMs={units(18)}
                >
                  <PrevNextGrid>
                    {data?.previousNews && (
                      <Box
                        display={'grid'}
                        gridTemplateRows={'max-content 1fr'}
                      >
                        <Subtitle
                          {...animProps}
                          marginBottom={units(2)}
                          size={'large'}
                        >
                          {translate('common:labels.prev')}
                        </Subtitle>

                        <div
                          data-scroll={'fade-in-up-small'}
                          data-scroll-animation-delay={'150'}
                          data-scroll-offset={'20%'}
                          key={data?.previousNews?.id}
                        >
                          <NewsExhibitionsCard
                            description={data?.previousNews?.lead}
                            imageUrl={data?.previousNews?.featuredImages}
                            slug={data?.previousNews?.slug}
                            title={data?.previousNews?.title}
                          />
                        </div>
                      </Box>
                    )}

                    {data?.nextNews && (
                      <Box
                        display={'grid'}
                        gridTemplateRows={'max-content 1fr'}
                      >
                        <Subtitle
                          {...animProps}
                          marginBottom={units(2)}
                          size={'large'}
                        >
                          {translate('common:labels.next')}
                        </Subtitle>

                        <div
                          data-scroll={'fade-in-up-small'}
                          data-scroll-animation-delay={'150'}
                          data-scroll-offset={'20%'}
                          key={data?.nextNews?.id}
                        >
                          <NewsExhibitionsCard
                            description={data?.nextNews?.lead}
                            imageUrl={data?.nextNews?.featuredImages}
                            slug={data?.nextNews?.slug}
                            title={data?.nextNews?.title}
                          />
                        </div>
                      </Box>
                    )}
                  </PrevNextGrid>
                </Box>
              )}
            </Grid>
          </StyledContainer>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `getStaticPaths`.
 */

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const newsExhibitions = await Promise.all(
    locales.map(
      async locale =>
        await fetchNewsExhibitions({
          locale,
          params: {
            limit: paginationLimit,
            page: 1
          }
        })
    )
  );

  const slugs = new Set<string>();
  newsExhibitions.flatMap(data =>
    data?.data?.forEach(({ slug }) => slugs.add(slug))
  );

  const paths = Array.from(slugs).map(slug => ({
    params: { slug }
  }));

  return {
    fallback: true,
    paths
  };
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<Props> = async ({
  locale,
  params
}) => {
  const slug = get(params, 'slug') as string;
  const data = await fetchNewsDetail({ locale, slug });

  if (!data) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      data
    },
    revalidate: 60
  };
};

/**
 * Export `NewsExhibitionsDetail` page.
 */

export default NewsExhibitionsDetail;
